import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import Api from "src/Config";

import { useDebounce } from "src/hooks/useDebonce";
import { textTruncate } from "src/helpers/truncateText";
import { format } from "date-fns";

export const ActivityLogsContext = createContext(null);

type Props = {
  children: ReactElement;
};

const ActivityLogsProvider: FunctionComponent<Props> = ({ children }) => {
  const { id } = useParams();
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const debouncedValue = useDebounce<string>(searchParam, 500);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["activity_logs", debouncedValue, pageNumber],
    staleTime: 30000,
    queryFn: async () => {
      const url = debouncedValue
        ? `tm/transactions/${id}/activity-logs?search=${debouncedValue}&page_size=10&page=1`
        : `tm/transactions/${id}/activity-logs?page_size=10&page=${
            pageNumber + 1
          }`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          email: string;
          phone: string;
          message: string;
          assignment_date: string;
          reason: string;
          created_at: string;

          user: {
            last_name: string;
            first_name: string;
            email: string;
            phone: string;
          };
        }) => ({
          id: res.id,
          "activity by": `${
            !res?.user?.first_name?.toLowerCase() &&
            !res?.user?.last_name?.toLowerCase()
              ? "N/A"
              : textTruncate(
                  `${res.user.first_name?.toLowerCase() || ""} ${
                    res.user.last_name?.toLowerCase() || ""
                  }`,
                  30,
                )
          }`,
          "activity done": `${
            res.message ? textTruncate(res.message, 30) : "-"
          }`,
          "date and time": res.created_at
            ? format(new Date(res.created_at), "dd MMM yyyy, hh:mm a")
            : "N/A",
          reason: res.reason,
          activity: res.message,
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const handleSearch = (e: any) => {
    setSearchParam(e.target.value);
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
    ],
  );
  return (
    <ActivityLogsContext.Provider value={contextValue}>
      {children}
    </ActivityLogsContext.Provider>
  );
};

export default ActivityLogsProvider;
